import {
    DriverActivityRuleText,
    DriverActivityRuleTextInput,
    DriverActivityRuleTemplate,
    DriverActivityRuleTemplateSection,
} from '../graphql/types';

/** 文案草稿：language -> sectionKey -> 原文（含 {{key}} 占位） */
export type RuleTextDraft = Record<string, Record<string, string>>;

/** 档位变量 key 的模式：targetDays{n} / rewardAmount{n}，n 从 1 起；chip 显示名按模式取 i18n（driverOps.activity.variables.{prefix}N 带 n） */
export const TIER_VARIABLE_PATTERN = /^(targetDays|rewardAmount)(\d+)$/;

/** 兜底模板用的档位上限（正常情况变量清单来自后端模板接口） */
const FALLBACK_MAX_LEVELS = 2;

/** 1..maxLevels 各档的变量 key，顺序与后端一致：档位 1 天数、档位 1 金额、档位 2 天数、… */
export function tierVariableKeys(maxLevels: number): string[] {
    const keys: string[] = [];
    for (let tier = 1; tier <= maxLevels; tier += 1) {
        keys.push(`targetDays${tier}`, `rewardAmount${tier}`);
    }
    return keys;
}

/**
 * 变量 chip 显示名：档位变量按模式取带 n 的 i18n（档位{{n}}天数 / 档位{{n}}金额），其它按 key 取；都找不到时显示 key 本身。
 * t 可以是界面语言的，也可以是 i18n.getFixedT(文案语种) 的。
 */
export function variableLabel(t: (key: string, opts?: Record<string, unknown>) => unknown, key: string): string {
    const tier = TIER_VARIABLE_PATTERN.exec(key);
    if (tier) {
        return String(t(`driverOps.activity.variables.${tier[1]}N`, { n: Number(tier[2]), defaultValue: key }));
    }
    return String(t(`driverOps.activity.variables.${key}`, { defaultValue: key }));
}

/**
 * 段标题（去掉中文预览用的尾部冒号）。t 传 i18n.getFixedT(文案语种) 时得到该语种的标题，和编辑器左列一致。
 */
export function sectionTitle(t: (key: string, opts?: Record<string, unknown>) => unknown, key: string): string {
    return String(t(`driverOps.activity.sections.${key}`, { defaultValue: key })).replace(/[：:]\s*$/, '');
}

/** 占位符格式 {{key}}，key 字母开头、可带数字（档位变量 targetDays1 / rewardAmount2），与后端 \w+ 口径一致 */
export const VARIABLE_PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9]*)\s*\}\}/g;

export interface RuleTextToken {
    type: 'text' | 'variable';
    value: string;
}

/**
 * 把一段文案拆成「普通文本 / 变量」两类 token，供编辑高亮与详情 chip 渲染。
 * 未闭合或 key 非法的花括号按普通文本处理。
 */
export function tokenizeRuleText(text: string): RuleTextToken[] {
    const tokens: RuleTextToken[] = [];
    if (!text) return tokens;
    const pattern = new RegExp(VARIABLE_PATTERN.source, 'g');
    let lastIndex = 0;
    let match: RegExpExecArray | null = pattern.exec(text);
    while (match) {
        if (match.index > lastIndex) {
            tokens.push({ type: 'text', value: text.slice(lastIndex, match.index) });
        }
        tokens.push({ type: 'variable', value: match[1] });
        lastIndex = match.index + match[0].length;
        match = pattern.exec(text);
    }
    if (lastIndex < text.length) {
        tokens.push({ type: 'text', value: text.slice(lastIndex) });
    }
    return tokens;
}

/** 变量占位符的原文形式 */
export function variablePlaceholder(key: string): string {
    return `{{${key}}}`;
}

/**
 * 在光标处插入变量占位符；有选区时替换选区。
 * 返回新文本和插入后的光标位置（占位符末尾），便于调用方恢复焦点。
 */
export function insertVariableAtCursor(
    text: string,
    selectionStart: number,
    selectionEnd: number,
    key: string
): { text: string; cursor: number } {
    const safeText = text ?? '';
    const start = Math.max(0, Math.min(selectionStart, safeText.length));
    const end = Math.max(start, Math.min(selectionEnd, safeText.length));
    const placeholder = variablePlaceholder(key);
    return {
        text: safeText.slice(0, start) + placeholder + safeText.slice(end),
        cursor: start + placeholder.length,
    };
}

/**
 * 退格 / Delete 时整体删除光标紧邻的变量占位符：
 * backward 看光标左侧是否正好是一个 {{key}} 的末尾，forward 看右侧是否正好是开头。
 * 不紧邻变量时返回 null，由浏览器按普通字符处理。
 */
export function removeVariableAtCursor(
    text: string,
    cursor: number,
    direction: 'backward' | 'forward'
): { text: string; cursor: number } | null {
    const safeText = text ?? '';
    let offset = 0;
    for (const token of tokenizeRuleText(safeText)) {
        const length = token.type === 'variable' ? variablePlaceholder(token.value).length : token.value.length;
        const start = offset;
        const end = offset + length;
        if (token.type === 'variable') {
            if ((direction === 'backward' && end === cursor) || (direction === 'forward' && start === cursor)) {
                return { text: safeText.slice(0, start) + safeText.slice(end), cursor: start };
            }
        }
        offset = end;
    }
    return null;
}

/** 字数按原文字符数计（含占位符），与后端 ≤300 的校验口径一致 */
export function ruleTextLength(text: string | undefined | null): number {
    return (text ?? '').length;
}

/** 按模板的语言 × 段构造空草稿 */
export function buildEmptyRuleText(languages: string[], sections: DriverActivityRuleTemplateSection[]): RuleTextDraft {
    const draft: RuleTextDraft = {};
    languages.forEach((lang) => {
        draft[lang] = {};
        sections.forEach((section) => {
            draft[lang][section.key] = '';
        });
    });
    return draft;
}

/** 后端返回的 list 结构 → 草稿；模板里有而后端没返回的段补空串 */
export function ruleTextFromList(
    list: DriverActivityRuleText[] | undefined | null,
    languages: string[],
    sections: DriverActivityRuleTemplateSection[]
): RuleTextDraft {
    const draft = buildEmptyRuleText(languages, sections);
    (list ?? []).forEach((item) => {
        if (!draft[item.language]) draft[item.language] = {};
        (item.sections ?? []).forEach((section) => {
            draft[item.language][section.key] = section.content ?? '';
        });
    });
    return draft;
}

/** 草稿 → 契约的 list 结构，按模板顺序输出每语每段 */
export function ruleTextToInput(
    draft: RuleTextDraft,
    languages: string[],
    sections: DriverActivityRuleTemplateSection[]
): DriverActivityRuleTextInput[] {
    const ordered = [...sections].sort((a, b) => a.order - b.order);
    return languages.map((language) => ({
        language,
        sections: ordered.map((section) => ({
            key: section.key,
            content: draft[language]?.[section.key] ?? '',
        })),
    }));
}

export interface RuleTextValidationError {
    lang: string;
    section: string;
    reason: 'required' | 'tooLong';
}

/** 三语每语：必填段非空、每段 ≤ sectionMaxLength */
export function validateRuleText(draft: RuleTextDraft, template: DriverActivityRuleTemplate): RuleTextValidationError[] {
    const errors: RuleTextValidationError[] = [];
    const ordered = [...template.sections].sort((a, b) => a.order - b.order);
    template.languages.forEach((lang) => {
        ordered.forEach((section) => {
            const content = (draft[lang]?.[section.key] ?? '').trim();
            if (section.required && !content) {
                errors.push({ lang, section: section.key, reason: 'required' });
            } else if (ruleTextLength(draft[lang]?.[section.key]) > template.sectionMaxLength) {
                errors.push({ lang, section: section.key, reason: 'tooLong' });
            }
        });
    });
    return errors;
}

/**
 * 模板查询失败时的兜底（与 admin-plan.md §六 一致），只为让页面仍可渲染；
 * 正常路径一律以 getDriverActivityRuleTemplate 返回为准。
 */
export const FALLBACK_RULE_TEMPLATE: DriverActivityRuleTemplate = {
    sections: [
        { key: 'activityTime', order: 1, required: true, defaultVariables: ['activityTime', 'weekCount'] },
        { key: 'target', order: 2, required: true, defaultVariables: ['scope'] },
        { key: 'standard', order: 3, required: true, defaultVariables: ['dailyQty'] },
        { key: 'reward', order: 4, required: true, defaultVariables: [...tierVariableKeys(FALLBACK_MAX_LEVELS), 'maxReward'] },
        { key: 'grant', order: 5, required: true, defaultVariables: [] },
        { key: 'notice', order: 6, required: false, defaultVariables: [] },
    ],
    variables: ['activityTime', 'weekCount', 'scope', 'dailyQty', 'maxReward', ...tierVariableKeys(FALLBACK_MAX_LEVELS)],
    languages: ['zh', 'en', 'es'],
    sectionMaxLength: 300,
    allowSignUpDuringActivity: true,
    defaultTexts: [],
};
